import { decodeErrorResult, isHex } from "viem";
import { providedProtocolErrorAbi } from "./providedErrors.js";
const noInputs = [];
const uint = (name) => ({ name, type: "uint256" });
const address = (name) => ({ name, type: "address" });
const bytes32 = (name) => ({ name, type: "bytes32" });
/** Canonical stable error ABI. Contract-specific generated ABIs may extend it. */
export const protocolErrorAbi = [
    ...providedProtocolErrorAbi,
    {
        type: "error",
        name: "InvalidState",
        inputs: [
            { name: "actual", type: "uint8" },
            { name: "expected", type: "uint8" },
        ],
    },
    { type: "error", name: "ProtocolPaused", inputs: noInputs },
    { type: "error", name: "InvalidAddress", inputs: [address("value")] },
    {
        type: "error",
        name: "UnexpectedDeploymentAddress",
        inputs: [address("expected"), address("actual")],
    },
    {
        type: "error",
        name: "InvalidQuantity",
        inputs: [uint("actual"), uint("minimum"), uint("maximum")],
    },
    { type: "error", name: "IncorrectPayment", inputs: [uint("expected"), uint("actual")] },
    { type: "error", name: "SoldOut", inputs: [uint("sold"), uint("cap")] },
    { type: "error", name: "FundraisingEnded", inputs: [uint("endTime"), uint("currentTime")] },
    { type: "error", name: "FundraisingNotEnded", inputs: [uint("endTime"), uint("currentTime")] },
    { type: "error", name: "GraduationThresholdNotMet", inputs: [uint("sold"), uint("required")] },
    { type: "error", name: "AlreadyFinalized", inputs: noInputs },
    { type: "error", name: "ArithmeticInvariant", inputs: noInputs },
    { type: "error", name: "ReentrantCall", inputs: noInputs },
    { type: "error", name: "Unauthorized", inputs: [address("caller")] },
    {
        type: "error",
        name: "InvalidBatchLength",
        inputs: [uint("actual"), uint("minimum"), uint("maximum")],
    },
    { type: "error", name: "AssetsAlreadyBound", inputs: noInputs },
    { type: "error", name: "PaymentAlreadyConfigured", inputs: noInputs },
    { type: "error", name: "UnauthorizedFactory", inputs: [address("caller")] },
    { type: "error", name: "UnauthorizedSafe", inputs: [address("caller")] },
    { type: "error", name: "UnauthorizedCreator", inputs: [address("caller")] },
    { type: "error", name: "InvalidNandSet", inputs: noInputs },
    { type: "error", name: "NandAlreadyConsumed", inputs: [uint("nandId")] },
    { type: "error", name: "NandAlreadyRefunded", inputs: [uint("nandId")] },
    {
        type: "error",
        name: "CommitAlreadyExists",
        inputs: [address("designer"), bytes32("commitment")],
    },
    { type: "error", name: "CommitMissing", inputs: [address("designer"), bytes32("commitment")] },
    { type: "error", name: "CommitTooFresh", inputs: [uint("commitBlock"), uint("currentBlock")] },
    {
        type: "error",
        name: "CommitNotExpired",
        inputs: [uint("deadlineBlock"), uint("currentBlock")],
    },
    { type: "error", name: "CommitExpired", inputs: [uint("deadlineBlock"), uint("currentBlock")] },
    { type: "error", name: "CommitmentMismatch", inputs: [bytes32("expected"), bytes32("actual")] },
    { type: "error", name: "CommitmentUsed", inputs: [address("designer"), bytes32("commitment")] },
    { type: "error", name: "InvalidNetlistLength", inputs: [uint("actual"), uint("expected")] },
    {
        type: "error",
        name: "InvalidNetlistOpcode",
        inputs: [uint("gateIndex"), { name: "opcode", type: "uint8" }],
    },
    {
        type: "error",
        name: "InvalidNetlistReference",
        inputs: [uint("gateIndex"), uint("referenceIndex")],
    },
    { type: "error", name: "UnreachableNetlistGate", inputs: [uint("gateIndex")] },
    { type: "error", name: "MinerAlreadyVerified", inputs: [uint("minerId")] },
    { type: "error", name: "MinerVerificationFailed", inputs: [uint("minerId")] },
    { type: "error", name: "ClaimsLockedUntilLiquidity", inputs: noInputs },
    {
        type: "error",
        name: "ExpiryBacklog",
        inputs: [{ name: "nextExpiry", type: "uint64" }, uint("currentTimestamp")],
    },
    { type: "error", name: "UnauthorizedFuelOperator", inputs: [address("caller"), uint("minerId")] },
    { type: "error", name: "InvalidFuelMultiplier", inputs: [uint("multiplier")] },
    { type: "error", name: "InvalidFuelPeriods", inputs: [uint("periods")] },
    { type: "error", name: "FuelRequiresVerifiedMiner", inputs: [uint("minerId")] },
    { type: "error", name: "FuelUnavailableBeforeActive", inputs: noInputs },
    {
        type: "error",
        name: "FuelPrepayExceeded",
        inputs: [uint("resultingPeriods"), uint("maximumPeriods")],
    },
    { type: "error", name: "ZeroFuelCost", inputs: noInputs },
    { type: "error", name: "SlippageExceeded", inputs: [uint("minimum"), uint("actual")] },
    { type: "error", name: "DeadlineExpired", inputs: [uint("deadline"), uint("currentTimestamp")] },
    { type: "error", name: "InvalidOfficialPair", inputs: [address("pair")] },
    {
        type: "error",
        name: "InvalidLiquidityMode",
        inputs: [
            { name: "actual", type: "uint8" },
            { name: "expected", type: "uint8" },
        ],
    },
    {
        type: "error",
        name: "LiquidityModeNotAllowed",
        inputs: [{ name: "mode", type: "uint8" }],
    },
    { type: "error", name: "PairAlreadyBound", inputs: [address("pair")] },
    { type: "error", name: "UnexpectedPairSupply", inputs: [uint("actualSupply")] },
    {
        type: "error",
        name: "InsufficientPairLiquidity",
        inputs: [uint("reserve0"), uint("reserve1")],
    },
    {
        type: "error",
        name: "RefundUnavailable",
        inputs: [{ name: "kind", type: "uint8" }, uint("tokenId")],
    },
    {
        type: "error",
        name: "AlreadyRefunded",
        inputs: [{ name: "kind", type: "uint8" }, uint("tokenId")],
    },
    { type: "error", name: "NothingToWithdraw", inputs: [address("beneficiary")] },
    {
        type: "error",
        name: "ExternalTransferFailed",
        inputs: [address("receiver"), uint("amount")],
    },
    { type: "error", name: "InvalidListing", inputs: [uint("listingId")] },
    { type: "error", name: "StaleListing", inputs: [uint("listingId")] },
    { type: "error", name: "PriceChanged", inputs: [uint("expected"), uint("actual")] },
    {
        type: "error",
        name: "TransferUnavailable",
        inputs: [{ name: "kind", type: "uint8" }, uint("tokenId")],
    },
    {
        type: "error",
        name: "InvalidPaymentAsset",
        inputs: [{ name: "kind", type: "uint8" }, address("token")],
    },
    { type: "error", name: "PaymentTokenNotAllowed", inputs: [address("token")] },
    {
        type: "error",
        name: "PaymentTransferMismatch",
        inputs: [uint("expected"), uint("received")],
    },
];
export const protocolErrorLocaleKeys = Object.freeze(Object.fromEntries(protocolErrorAbi.map(({ name }) => [name, `errors.${name}`])));
function findRevertData(error, depth = 0) {
    if (depth > 8 || typeof error !== "object" || error === null)
        return undefined;
    if ("data" in error && typeof error.data === "string" && isHex(error.data))
        return error.data;
    if ("cause" in error)
        return findRevertData(error.cause, depth + 1);
    return undefined;
}
export function decodeProtocolError(errorOrData) {
    const data = typeof errorOrData === "string" && isHex(errorOrData)
        ? errorOrData
        : findRevertData(errorOrData);
    if (data === undefined)
        return undefined;
    try {
        const decoded = decodeErrorResult({ abi: protocolErrorAbi, data });
        const code = decoded.errorName;
        return {
            code,
            localeKey: protocolErrorLocaleKeys[code],
            args: decoded.args ?? [],
            data,
        };
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=errors.js.map